import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Brain, Puzzle, Clock, BookOpen, Search, Grid, Target, Shuffle, Layers, Shield, Zap, X, Filter, LayoutGrid, CheckCircle
} from "lucide-react"; 


import shapematch from "../Photos/Puzzles/ShapeMatch.png"
import maze from "../Photos/Puzzles/Maze.png"
import memorymaster from "../Photos/Puzzles/Memory.png"
import rapidfire from "../Photos/Puzzles/RapidFire.png"
import sudoku from "../Photos/Puzzles/Sudoku.png"
import wordscramble from "../Photos/Puzzles/WordScramble.png"


// --- Framer Motion Variants (Clean & Professional) ---
const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
        opacity: 1, 
        transition: { 
            staggerChildren: 0.08, 
            when: "beforeChildren" 
        } 
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { 
            duration: 0.5, 
            ease: "easeOut"
        } 
    },
};

const sidebarItemVariants = {
    active: { scale: 1.05, background: "rgba(37, 99, 235, 0.1)", color: "#2563EB", transition: { type: "spring", stiffness: 300 } },
    inactive: { scale: 1, background: "rgba(255, 255, 255, 0)", color: "#4B5563" },
};


// --- Puzzle Data (Unchanged) ---
const ALL_PUZZLES = [
    {
        id: 201,
        title: "The Great Shape Match 🟥",
        desc: "Match shapes and complete visual patterns.",
        category: "Visual Logic",
        icon: Grid,
        level: "Easy",
        imageUrl: shapematch,
    },
    {
        id: 202,
        title: "Sudoku Starters 🌟",
        desc: "Small grid Sudoku for number sense practice.",
        category: "Number Logic",
        icon: BookOpen,
        level: "Medium",
        imageUrl: sudoku,
    },
    {
        id: 203,
        title: "Word Scramble Sprint 🏃",
        desc: "Race the clock to unscramble the letters!",
        category: "Word Games",
        icon: Shuffle,
        level: "Hard",
        imageUrl: wordscramble,
    },
    {
        id: 204,
        title: "Memory Master Challenge ✨",
        desc: "Test your focus by matching all the pairs.",
        category: "Focus & Memory",
        icon: Target,
        level: "Easy",
        imageUrl: memorymaster,
    },
    {
        id: 205,
        title: "The Logic Labyrinth Maze 🧩",
        desc: "Deduce the path through a complex logical maze.",
        category: "Abstract Thinking",
        icon: Layers,
        level: "Hard",
        imageUrl: maze,
    },
     {
        id: 206,
        title: "Rapid Fire Arithmetics ⏱️",
        desc: "Quickly solve math problems under pressure.",
        category: "Timed Challenges",
        icon: Clock,
        level: "Medium",
        imageUrl: rapidfire,
    },
];

const PUZZLE_CATEGORIES = [
    { name: "All Puzzles", icon: Brain },
    { name: "Visual Logic", icon: Grid },
    { name: "Number Logic", icon: BookOpen },
    { name: "Word Games", icon: Shuffle },
    { name: "Focus & Memory", icon: Target },
    { name: "Abstract Thinking", icon: Layers },
    { name: "Timed Challenges", icon: Clock },
];

const PuzzleCard = ({ puzzle }) => {
    const IconComponent = puzzle.icon;

    // Cleaned up level colors
    let levelClasses = "font-semibold text-xs rounded-lg px-2 py-1";
    let buttonColor = "bg-blue-600 hover:bg-blue-700 shadow-blue-300/50";
    
    if (puzzle.level === "Easy") {
        levelClasses += " bg-green-500 text-white";
    }
    else if (puzzle.level === "Medium") {
        levelClasses += " bg-yellow-500 text-gray-800";
    }
    else if (puzzle.level === "Hard") {
        levelClasses += " bg-red-500 text-white";
    }

    return (
        <motion.article
            variants={itemVariants}
            layout
            // Clean, soft shadow, and standard radius
            className="flex flex-col bg-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] border border-gray-100 overflow-hidden w-full"
            whileHover={{ y: -3 }}
        >
<div className="relative h-80 sm:h-96 md:h-[28rem] overflow-hidden rounded-2xl">

    {/* Image */}
    <div 
        className="w-full h-full flex items-center justify-center"
        style={{ 
            backgroundImage: `url(${puzzle.imageUrl})`,
            backgroundSize: "contain",      // ← full image visible
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#eef2ff"      // light fallback background
        }}
    >
    </div>

    {/* Level Tag */}
    <div className={`absolute top-4 left-4 ${levelClasses}`}>
        {puzzle.level}
    </div>

</div>



            <div className="p-5 flex flex-col flex-grow">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{puzzle.title}</h2>
                    <IconComponent className="w-6 h-6 text-indigo-500" />
                </div>
                
                <p className="text-gray-500 text-sm mb-4 flex-grow">{puzzle.desc}</p>

                {/* Category Chip - Subdued and clean */}
                <p className="text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full inline-block self-start mb-4">
                    {puzzle.category}
                </p>
            </div>
            
            {/* Start Button - Clean, standard style */}
            <motion.button 
                className={`mx-5 mb-5 py-3 text-white font-semibold text-lg rounded-lg ${buttonColor} flex items-center justify-center gap-2`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <Zap className="w-5 h-5" />
                Start Challenge
            </motion.button>
        </motion.article>
    );
};

// --- Main Component ---
export default function TalkyToonPuzzleCorner() {
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState("All Puzzles");

    const filteredPuzzles = useMemo(() => {
        return ALL_PUZZLES.filter((p) => {
            const categoryMatch = activeCategory === "All Puzzles" || p.category === activeCategory;
            const searchMatch =
                p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.desc.toLowerCase().includes(searchTerm.toLowerCase());
            return categoryMatch && searchMatch;
        });
    }, [searchTerm, activeCategory]);

    const resetFilters = () => {
        setSearchTerm("");
        setActiveCategory("All Puzzles");
    };


    return (
        // Clean, educational background
        <div className="min-h-screen w-full bg-gray-50 text-gray-800 overflow-x-hidden">

            {/* Header / Search Bar (Top Bar) */}
            <header className="sticky top-0 z-20 w-full bg-white shadow-md">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h1 className="text-3xl font-extrabold text-blue-600 flex items-center gap-2">
                        <Puzzle className="w-8 h-8 text-indigo-500" />
                        Puzzle Hub
                    </h1>

                    <div className="relative w-full max-w-lg hidden sm:block">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search puzzles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full py-2.5 pl-12 pr-12 text-base rounded-full border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                        />
                         {(searchTerm || activeCategory !== "All Puzzles") && (
                            <motion.button
                                onClick={resetFilters}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-500 hover:text-red-500"
                                title="Clear Filters"
                                whileTap={{ scale: 0.8 }}
                            >
                                <X className="w-5 h-5" />
                            </motion.button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content: Sidebar and Grid */}
            <div className="flex flex-col lg:flex-row w-full max-w-full">
                
                {/* Sidebar (Categories/Filters) - Fixed width, border, and background */}
                <aside className="w-full lg:w-64 flex-shrink-0 p-6 bg-white border-r border-gray-200 lg:h-screen lg:sticky lg:top-[5.5rem] shadow-xl lg:shadow-none">
                    <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2 border-b pb-2">
                        <Filter className="w-5 h-5 text-blue-500" />
                        Puzzle Categories
                    </h2>
                    
                    <nav className="flex flex-wrap lg:flex-col gap-2">
                        {PUZZLE_CATEGORIES.map((cat) => {
                            const isActive = activeCategory === cat.name;
                            const Icon = cat.icon;
                            return (
                                <motion.button
                                    key={cat.name}
                                    onClick={() => setActiveCategory(cat.name)}
                                    className={`w-full text-left px-4 py-2 rounded-lg flex items-center gap-3 font-semibold text-sm transition-colors duration-200`}
                                    variants={sidebarItemVariants}
                                    animate={isActive ? "active" : "inactive"}
                                    whileHover={{ scale: 1.02, backgroundColor: "rgba(243, 244, 246, 1)" }}
                                >
                                    <Icon className="w-5 h-5 flex-shrink-0" />
                                    {cat.name}
                                    {isActive && <CheckCircle className="w-4 h-4 text-blue-500 ml-auto" />}
                                </motion.button>
                            );
                        })}
                    </nav>
                </aside>

                {/* Puzzle Grid Area */}
                <main className="flex-grow p-6 sm:p-8">
                    <h2 className="text-3xl font-extrabold text-gray-800 mb-6 border-b pb-2">
                        {activeCategory}
                        <span className="text-blue-500 ml-2">Challenges</span>
                    </h2>
                    
                    {/* Grid */}
                    <AnimatePresence mode="wait">
                        {filteredPuzzles.length > 0 ? (
                            <motion.div
                                key="puzzle-grid"
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                            >
                                <AnimatePresence>
                                    {filteredPuzzles.map((p) => (
                                        <PuzzleCard key={p.id} puzzle={p} />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                        ) : (
                            <motion.div 
                                key="no-results"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                className="text-center py-16 bg-white rounded-xl shadow-inner border border-red-200 mt-6"
                            >
                                <Search className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-gray-700 mb-2">Search Did Not Match</h3>
                                <p className="text-lg text-gray-500">
                                    No puzzles found in the {activeCategory} category matching "{searchTerm}".
                                </p>
                                <button
                                    onClick={resetFilters}
                                    className="mt-6 px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors shadow-md"
                                >
                                    View All Puzzles
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </main>
            </div>
        </div>
    );
}